/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Component, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AccessType } from "@quintype/components";
import get from "lodash/get";
import "./user-details.m.css";
import { UserProfile } from "./user-profile";
import { SubscriptionsBlock } from "./subscription-details";
import { PurchasedStoriesBlock } from "./purchase-stories";
import { getLoginLink } from "../login/sso-utils";
import LoaderOverlay from "../../molecules/loader-overlay";

const InfoMessage = ({ localizedSignIn, localizedSignInMsg, sso, member }) => {
  return (
    <div styleName="info-message-wrapper">
      <h3>{localizedSignInMsg || "You must sign in to access this page."}</h3>
      <div styleName="button" onClick={() => getLoginLink(member, sso)}>
        {localizedSignIn || "Sign In"}
      </div>
    </div>
  );
};

InfoMessage.propTypes = {
  localizedSignIn: PropTypes.string,
  localizedSignInMsg: PropTypes.string,
  sso: PropTypes.shape({
    enableSingleSignOn: PropTypes.bool,
    bridgeKeeperClientId: PropTypes.string,
  }),
};

const UserContent = ({
  enableAccesstype,
  currentScreen,
  isAccessTypeLoaded,
  member,
  getSubscriptionForUser,
  loadSubscriptionMenu,
  myProfile,
  mySubscriptions,
  purchasedStories,
  localizedMonths,
  memberSubscriptions,
}) => {
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true);

  const getUserSubscriptions = async () => {
    const { subscriptions } = (await getSubscriptionForUser()) || [];
    setIsSubscriptionsLoading(false);
    loadSubscriptionMenu(true);
    subscriptions && setUserSubscriptions(subscriptions);
  };

  useEffect(() => {
    enableAccesstype && isAccessTypeLoaded && !userSubscriptions.length && getUserSubscriptions();
    !enableAccesstype && setIsSubscriptionsLoading(false);
  }, [isAccessTypeLoaded, currentScreen, enableAccesstype, memberSubscriptions]);

  switch (currentScreen) {
    case "Profile":
      return (
        <UserProfile
          subscriptions={userSubscriptions}
          isSubscriptionsLoading={isSubscriptionsLoading}
          member={member}
          enableAccesstype={enableAccesstype}
          myProfile={myProfile}
          localizedMonths={localizedMonths}
        />
      );
    case "Subscription":
      return (
        <SubscriptionsBlock
          subscriptions={userSubscriptions}
          mySubscriptions={mySubscriptions}
          localizedMonths={localizedMonths}
        />
      );
    case "PurchasedStories":
      return (
        <PurchasedStoriesBlock
          subscriptions={userSubscriptions}
          purchasedStories={purchasedStories}
          localizedMonths={localizedMonths}
        />
      );
    default:
      return null;
  }
};

UserContent.propTypes = {
  isAccessTypeLoaded: PropTypes.bool,
  enableAccesstype: PropTypes.bool,
  getSubscriptionForUser: PropTypes.func,
  member: PropTypes.object,
  subscriptions: PropTypes.arrayOf(PropTypes.object),
  currentScreen: PropTypes.string,
  loadSubscriptionMenu: PropTypes.func,
  localizedSubscribe: PropTypes.string,
  localizedMonths: PropTypes.object,
};

const userMenu = (
  enableAccesstype = false,
  enablePayPerAsset = false,
  enableSubscriptionMenu = false,
  localizedElements = {}
) => {
  const { myProfile = {}, mySubscriptions = {}, purchasedStories = {} } = localizedElements;
  return [
    {
      name: "Profile",
      label: myProfile.tabName || "Profile",
      active: "true",
    },
    {
      name: "Subscription",
      label: mySubscriptions.tabName || "My Subscriptions",
      active: enableAccesstype && enableSubscriptionMenu,
    },
    {
      name: "PurchasedStories",
      label: purchasedStories.tabName || "Purchased Stories",
      active: enablePayPerAsset && enableSubscriptionMenu,
    },
  ];
};

class UserDetailsBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
      currentScreen: "Profile",
      enableSubscriptionMenu: false,
    };
    this.loadSubscriptionMenu = this.loadSubscriptionMenu.bind(this);
  }

  loadSubscriptionMenu = () => {
    this.setState({ enableSubscriptionMenu: true });
  };

  handleClick = (currentScreen, index) => {
    this.setState({ activeIndex: index });
    this.setState({ currentScreen });
  };

  render() {
    const user = this.props.member;
    const { localization = {}, sso = {} } = get(this.props, ["config", "pagebuilder-config", "general"], {});
    const { enableLocalization = false, localizedElements = {} } = localization;
    const { signInSignUp = {}, myProfile = {} } = enableLocalization ? localizedElements : {};
    const { signIn: localizedSignIn } = signInSignUp;
    const { signInMessage: localizedSignInMsg } = myProfile;

    if (!this.props.memberLoaded) return <LoaderOverlay />;

    if (user && user["verification-status"]) {
      const { email = "", "phone-number": phone = "" } = user;
      const { accesstypeIntegration: PbAccessTypeConfig = {}, localization = {} } = get(
        this.props,
        ["config", "pagebuilder-config", "general"],
        {}
      );

      const isStaging = get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging";
      const {
        accesstypeKey = "",
        bridgeKeeperIntegrationId = "",
        enableAccesstype = false,
        disableMetering = true,
        enablePayPerAsset = false,
        accessTypeProdHost = "",
        accessTypeStgHost = "",
      } = PbAccessTypeConfig;

      const isPayPerAssetEnabled = enableAccesstype && enablePayPerAsset;
      const { enableLocalization = false, localizedElements = {} } = localization;
      const {
        buttonLabels = {},
        myProfile = {},
        mySubscriptions = {},
        purchasedStories = {},
        months = {},
      } = enableLocalization ? localizedElements : {};
      const { subscribe: localizedSubscribe } = buttonLabels;

      return (
        <div styleName="user-detail-wrapper ">
          <div styleName="placeholder-components">
            {userMenu(
              enableAccesstype,
              isPayPerAssetEnabled,
              this.state.enableSubscriptionMenu,
              enableLocalization ? localizedElements : {}
            ).map((menu, index) => {
              return (
                <div
                  key={menu.name}
                  styleName={this.state.activeIndex === index ? "active-comp placeholder" : "placeholder"}
                  onClick={() => this.handleClick(menu.name, index)}
                >
                  {menu.active && <span styleName="name">{menu.label}</span>}
                </div>
              );
            })}
          </div>

          <div styleName="linked-components">
            <AccessType
              enableAccesstype={enableAccesstype}
              isStaging={isStaging}
              accessTypeKey={accesstypeKey}
              accessTypeBkIntegrationId={bridgeKeeperIntegrationId}
              email={email}
              phone={phone}
              disableMetering={disableMetering}
              prodHost={accessTypeProdHost}
              stagingHost={accessTypeStgHost}
            >
              {({ getSubscriptionForUser }) => {
                return (
                  <UserContent
                    enableAccesstype={enableAccesstype}
                    member={user}
                    currentScreen={this.state.currentScreen}
                    getSubscriptionForUser={getSubscriptionForUser}
                    isAccessTypeLoaded={!!global.AccessType} // TODO: AT should provide this flag
                    loadSubscriptionMenu={this.loadSubscriptionMenu}
                    localizedSubscribe={localizedSubscribe}
                    myProfile={myProfile}
                    mySubscriptions={mySubscriptions}
                    purchasedStories={purchasedStories}
                    localizedMonths={months}
                    memberSubscriptions={this.props.memberSubscriptions}
                  />
                );
              }}
            </AccessType>
          </div>
        </div>
      );
    } else {
      return (
        <InfoMessage
          localizedSignIn={localizedSignIn}
          localizedSignInMsg={localizedSignInMsg}
          sso={sso}
          member={user}
        />
      );
    }
  }
}

const mapStateToProps = (state) => {
  return {
    config: get(state, ["qt", "config"]),
    member: get(state, ["member"], {}),
    memberSubscriptions: get(state, ["memberSubscriptions"]),
    memberLoaded: get(state, ["memberLoaded"], false),
  };
};

UserDetailsBase.propTypes = {
  config: PropTypes.object,
  member: PropTypes.shape({
    email: PropTypes.string,
    "phone-number": PropTypes.string,
    "verification-status": PropTypes.string,
  }),
};

const UserDetails = connect(mapStateToProps, () => ({}))(UserDetailsBase);

export { UserDetails };
